import DateHelpers from '../helpers/dateHelpers';
import { useEffect, useMemo, useState } from 'react';
import { ScreenSkeleton } from '../components/atoms/ScreenSkeleton';
import { Button } from '../components/atoms/Button';
import { useNavigate, useParams } from 'react-router';
import { ErrorBanner } from '../components/atoms/ErrorBanner';
import {
  TableOfContentsRenderer,
  TableOfContentsSection,
} from '../components/templates/TableOfContentsRenderer';
import { TaskStatus } from '../data/enums';
import { routePaths } from '../routes/basePaths';
import { Label } from '../components/atoms/Label';
import { EquipmentCard } from '../components/molecules/EquipmentCard';
import useGetInspectionCategoryType from '../hooks/useInspectionCategoryType';
import { useAssetMineLocationLevel } from '../hooks/useAssetMineLocationLevel';
import DataErrorModalContent from '../components/organisms/DataErrorModalContent';
import { useInspectionCached } from '../hooks/useInspectionCached';
import { InspectionModelHelpers } from '../helpers/inspection-model-helpers';
import { useCurrentInspectionAssetEquipmentCached } from '../hooks/useCurrentInspectionAssetEquipmentCached';
import useInspectionTableOfContentsIndicators from '../hooks/progressIndicators/useInspectionTableOfContentsIndicators';
import { ScreenHeading } from '../components/molecules/ScreenHeading';

interface InspectionData {
  id: string;
  assetName: string;
  assetType: string;
  assetMode: string;
  siteName: string;
  customerName: string;
  timestamp: string;
  sections: TableOfContentsSection[];
}

export const InspectionTableOfContentsScreen = () => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const [sections, setSections] = useState<TableOfContentsSection[]>([]);

  const { inspection } = useInspectionCached(externalId);

  const inspectionCategoryType = useGetInspectionCategoryType({
    inspection,
  });

  const inspectionAssetEquipmentState =
    useCurrentInspectionAssetEquipmentCached({
      inspectionId: externalId ?? '',
    });

  const assetMineLocationState = useAssetMineLocationLevel({
    inspectionId: externalId,
  });

  const {
    inspectionDateIndicator,
    machinePhotoIndicator,
    operationIndicator,
    inspectionSummaryIndicator,
    undercarriageIndicator,
    bucketIndicator,
  } = useInspectionTableOfContentsIndicators(externalId ?? '');

  const hasUndercarriage = useMemo(() => {
    try {
      if (!inspection || !inspection.asset || !inspection.asset.equipments) {
        return false;
      }
      const undercarriageEquipments = inspection.asset.equipments.filter(
        (equipment) => equipment.equipmentCategory === 'Undercarriage',
      );
      return undercarriageEquipments.length > 0;
    } catch (error) {
      console.error('Error checking undercarriage availability:', error);
      return false;
    }
  }, [inspection]);

  const disabledMessage = useMemo(() => {
    if (inspection?.inspection.equipmentExternalId) {
      return 'Undercarriage can not be inspected in a yard';
    } else if (!hasUndercarriage) {
      return 'Undercarriage is not available for this asset';
    }
    return undefined;
  }, [inspection?.inspection.equipmentExternalId]);

  useEffect(() => {
    if (inspection) {
      //Example of progress indicator calculation

      const inspectionData: InspectionData = {
        id: inspection.inspection.externalId,
        assetName: inspection.asset
          ? inspection.asset.name
          : (inspection.mineLocation?.mineLocation.name ?? ''),
        assetType: inspection.asset?.model.assetType ?? 'Mine location',
        assetMode: inspection.asset?.model.name ?? 'Mine location',
        siteName: inspection.asset
          ? inspection.asset.siteName
          : (inspection.mineLocation?.siteName ?? ''),
        customerName: inspection.asset
          ? inspection.asset.customerName
          : (inspection.mineLocation?.customerName ?? ''),
        timestamp: DateHelpers.displayShortTimestamp({
          timestamp: inspection.inspection.inspectionDate,
          convertToCommonTime: inspection.inspection.isSynced,
        }),
        sections: [
          {
            id: 'basics',
            order: 1,
            title: '',
            items: [
              {
                id: 'inspection-date',
                order: 1,
                label: 'Inspection date',
                status: inspectionDateIndicator,
                url: 'inspection-date',
                enabled: true,
                inspectionId: inspection.inspection.externalId,
                inspectionCategoryTypeId:
                  inspectionCategoryType?.externalId ?? '',
              },
              {
                id: 'machine-photo',
                order: 1,
                label: 'Machine photo',
                status: machinePhotoIndicator,
                url: 'asset/machine-photo',
                enabled: inspection.asset !== undefined,
                disabledMessage: inspection.inspection.equipmentExternalId
                  ? 'Undercarriage can not be inspected in a yard'
                  : undefined,
                inspectionId: inspection.inspection.externalId,
                inspectionCategoryTypeId:
                  inspectionCategoryType?.externalId ?? '',
              },
              {
                id: 'operation',
                order: 2,
                label: 'Operation',
                status: operationIndicator ?? TaskStatus.notStarted,
                url: 'asset/operating-hours',
                enabled: inspection.asset !== undefined,
                disabledMessage: inspection.inspection.equipmentExternalId
                  ? 'Undercarriage can not be inspected in a yard'
                  : undefined,
                inspectionId: inspection.inspection.externalId,
                inspectionCategoryTypeId:
                  inspectionCategoryType?.externalId ?? '',
              },
            ],
          },
          {
            id: 'attachments',
            order: 5,
            title: '',
            items: [
              {
                id: 'inspection-summary',
                order: 2,
                label: 'Inspection summary',
                status: inspectionSummaryIndicator ?? TaskStatus.notStarted,
                url: `asset/${routePaths.ExecutiveSummary}`,
                enabled: true,
                inspectionId: inspection.inspection.externalId,
                inspectionCategoryTypeId:
                  inspectionCategoryType?.externalId ?? '',
              },
            ],
          },
        ],
      };

      if (inspectionCategoryType) {
        const undercarriageSections: TableOfContentsSection = {
          id: 'condition',
          order: 2,
          title: 'Condition',
          tooltipKey: 'inspection-toc-condition',
          items: [
            {
              id: 'undercarriage',
              order: 1,
              label: 'Undercarriage',
              status: undercarriageIndicator ?? TaskStatus.notStarted,
              url: 'undercarriage',
              enabled: inspection.asset !== undefined && hasUndercarriage,
              disabledMessage,
              showErrorIcon: !hasUndercarriage,
              disabledModal: !hasUndercarriage
                ? {
                    title: 'No Undercarriage Found',
                    children: (
                      <DataErrorModalContent
                        content={[
                          'This asset does not have an undercarriage.',
                          'If this is an error, please contact digitalsupport@bradken.com',
                        ]}
                        showSlackForm={true}
                        metadata={{
                          urlPath: window.location.href,
                          openReplayUuid:
                            localStorage.getItem('__openreplay_uuid'),
                          assetId: inspection.inspection.assetId,
                          customerName: inspection.asset?.customerName,
                          siteName: inspection.asset?.siteName,
                          assetName: inspection.asset?.name,
                          asset:
                            inspection.asset !== undefined
                              ? {
                                  externalId: inspection.asset.externalId,
                                  name: inspection.asset.name,
                                  type: inspection.asset.model.assetType,
                                  typeId: inspection.asset.assetTypeId,
                                  modelName: inspection.asset.model.name,
                                  modelId: inspection.asset.model.id,
                                  model: {
                                    Id: inspection.asset.model.id,
                                    name: inspection.asset.model.name,
                                    assetType: inspection.asset.model.assetType,
                                  },
                                }
                              : undefined,
                        }}
                      />
                    ),
                  }
                : undefined,
              inspectionId: inspection.inspection.externalId,
              inspectionCategoryTypeId: inspectionCategoryType.externalId,
            },
          ],
        };

        inspectionData.sections.push(undercarriageSections);
      }

      if (inspectionCategoryType) {
        if (
          inspectionAssetEquipmentState.bucket !== null &&
          inspectionAssetEquipmentState.bucket
        ) {
          const conditionAndChangeoutSection: TableOfContentsSection = {
            id: 'condition-and-changeout',
            order: 3,
            title: 'Condition & changeout',
            items: [],
            tooltipKey: 'inspection-toc-condition-and-changeout',
          };
          conditionAndChangeoutSection.items.push({
            id: 'bucket',
            order: 2,
            label: 'Bucket',
            status: bucketIndicator ?? TaskStatus.notStarted,
            url: 'bucket',
            enabled: true,
            inspectionId: inspection.inspection.externalId,
            inspectionCategoryTypeId: inspectionCategoryType.externalId,
          });

          inspectionData.sections.push(conditionAndChangeoutSection);
        }
      }

      setSections(inspectionData.sections);
    }
  }, [
    inspection,
    inspectionAssetEquipmentState.bucket,
    inspectionCategoryType,
    inspectionSummaryIndicator,
    machinePhotoIndicator,
    operationIndicator,
    inspectionDateIndicator,
    undercarriageIndicator,
    bucketIndicator,
  ]);

  if (
    !inspection ||
    inspectionAssetEquipmentState.isLoading ||
    assetMineLocationState.isLoading
  ) {
    return <ScreenSkeleton fields={8} />;
  }

  if (assetMineLocationState.error || inspectionAssetEquipmentState.error) {
    return (
      <ErrorBanner
        message={
          assetMineLocationState.error ||
          inspectionAssetEquipmentState.error ||
          ''
        }
      />
    );
  }

  // An inspection synced without category types (#10006) has nowhere to store
  // observations — every entry screen's save would throw — so all navigation
  // into it is gated below and an explanatory notice shown instead. A later
  // sync that delivers its categories rebuilds it fully.
  const hasCategories = InspectionModelHelpers.hasCategoryTypes(
    inspection.inspection,
  );

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex flex-col">
        <ScreenHeading title="Inspection" fullDate />

        {hasCategories && inspectionAssetEquipmentState.bucket !== null && (
          <Button
            variant="outline"
            onClick={() => navigate('bucket')}
            className="mt-8"
          >
            Edit / Changeout Bucket
          </Button>
        )}

        {hasCategories && inspectionAssetEquipmentState.bucket === null && (
          <Button
            variant="outline"
            onClick={() => navigate('bucket/new-bucket')}
            className="mt-8"
          >
            Add bucket
          </Button>
        )}

        {inspectionAssetEquipmentState.bucket && (
          <div className="mt-4 px-4">
            <Label titleType="subHeader">Bucket</Label>
            <div className="mt-4 w-full bg-white">
              <EquipmentCard equipment={inspectionAssetEquipmentState.bucket} />
            </div>
          </div>
        )}
      </div>

      {/* ToC Content */}
      {hasCategories ? (
        <TableOfContentsRenderer
          sections={sections}
          header={null}
          footer={
            <div className="flex flex-col items-center justify-center space-y-4 px-4">
              <Button
                variant="primary"
                onClick={() => navigate('review')}
                className="mt-8"
              >
                REVIEW AND SUBMIT
              </Button>
            </div>
          }
        />
      ) : (
        <div className="mx-4 mt-8 rounded-lg bg-brandLight/30 p-4 text-center text-ink">
          This inspection has no inspection categories, so it cannot be
          completed on this device. It may not have finished syncing — run Sync
          Data and reopen it, or contact your administrator if the problem
          persists.
        </div>
      )}
    </div>
  );
};
